import React from "react";

import classNames from "classnames";
import { ClickAwayListener, Dialog, Grid, IconButton, makeStyles, Paper, 
	Popover, Popper, Slide, Theme, Toolbar, Tooltip, Typography } from "@material-ui/core";

import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CloseIcon from "@material-ui/icons/Close";

import { TelemetryService } from "../../services/TelemetryService";
import { GTag } from "../../utilities/Tracking/GoogleAnalytics";

import { LibraryItem } from "../../entities/LibraryItem";
import { createContext, useEffect, useMemo, useState } from "react";
import { useIsMobile } from "../../components/Mui3WidthProvider";
import { useLibrary } from "./LibraryPage";
import DateHelper from "../../utilities/DateHelper";
import { useHistory } from "react-router-dom";
import { LibraryItemType } from "../../entities/LibraryItemType";
import { routes } from "../../routes";
import { LinkAssetType } from "../../entities/LinkAssetType";
import { FileAssetService } from "../../services/FIleAssetService";
import { ShowEmbeddedLinkDialog } from "./Views/Components/ShowEmbeddedLinkDialog";
import { LibraryAssetActions } from "./LibraryAssetActions/LibraryAssetActions";
import { GridGrow } from "../../components/GridGrow";
import { PageTitle } from "../../components/PageTitle";
import { MobileLibraryAssetTable } from "./Views/Components/MobileLibraryAssetTable";
import { LoadingDialog } from "../../components/LoadingDialog";

const useStyles = makeStyles((theme: Theme) => ({
	currentMonthText: {
		fontSize: "20px",
		color: "#5f6368",
		fontWeight: 500,
		fontFamily: "Roboto, Helvetica",
	},
	tableCellWeekdays: {
		border: "1px solid rgba(224, 224, 224, 1)",
		padding: "10px",
		color: "#5f6368",
	},
	table: {
		borderCollapse: "collapse",
		width: "100%",
		fontWeight: 500,
		fontFamily: "Roboto, Helvetica",
		verticalAlign: "middle",
		tableLayout: "fixed",
	},
	tableHeaderRow: {
		height: "100px",
	},
	tableCellDay: {
		border: "1px solid rgba(224, 224, 224, 1)",
		position: "relative",
		textAlign: "left",
		paddingLeft: "4px",
		"&:hover": {
			backgroundColor: "rgb(33,150,243, .2)",
		},
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		[theme.breakpoints.down("sm")]: {
			paddingLeft: "0px",
		},
	},
	tableCellDayCount: {
		top: "3px",
		left: "3px",
		position: "absolute",
		color: "#5f6368",
		fontSize: "12px",
		cursor: "context-menu",
	},
	outOfMonthCell: {
		backgroundColor: "rgba(224, 224, 224, 1)",
		"&:hover": {
			backgroundColor: "rgba(224, 224, 224, 1)",
		},
	},
	todayCell: {
		backgroundColor: "rgb(66, 133, 244)",
		padding: "3px 6px 3px 6px",
		borderRadius: "50%",
		color: "#FFF",
		fontWeight: 400,
	},
	tableTextRow: {
		fontWeight: 500,
		cursor: "pointer",
		color: "#5f6368",
		display: "flex",
	},
	mobileTableTextRow: {
		fontWeight: 500,
		cursor: "pointer",
		color: "#5f6368",
		display: "flex",
		"&:not(:first-of-type)": {
			marginTop: "2px",
		},
	},
	tableTextBulletHolder: {
		display: "flex",
		justifyContent: "center",
		marginRight: "6px",
		paddingTop: "5px",
	},
	tableTextBullet: {
		borderColor: "#039BE5",
		borderRadius: "8px",
		border: "4px solid",
		height: 0,
		width: 0,
	},
	tableText: {
		overflow: "hidden",
		whiteSpace: "nowrap",
		flex: "0 1 auto",
		textOverflow: "ellipsis",
	},
	mobileTableText: {
		overflow: "hidden",
		whiteSpace: "nowrap",
		flex: "0 1 auto",
		fontSize: "12px",
		backgroundColor: "#039BE5",
		color: "#FFF",
		padding: 2,
		borderRadius: 2,
	},
	tableCellContent: {
		maxHeight: "80px",
		marginTop: "12px",
	},
	seeMoreText: {
		color: "#757575",
		fontSize: "12px",
		fontWeight: 400,
		cursor: "pointer",
	},
	popOverHeader: {
		flex: "none",
		padding: "0 0 6px 12px",
		flexDirection: "row",
		justifyContent: "space-between",
		display: "flex",
	},
	popOverClose: {
		order: 1,
		padding: "4px 4px 0 0",
	},
	popOverDateHolder: {
		order: 0,
		paddingTop: "12px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start",
		fontWeight: 400,
		margin: 0,
	},
	popOverDayOfWeek: {
		fontSize: "12px",
		lineHeight: "12px",
		paddingBottom: "6px",
		color: "#616161",
	},
	popOverDayOfMonth: {
		fontSize: "40px",
		lineHeight: "40px",
		color: "#9e9e9e",
		cursor: "pointer",
		position: "relative",
		outline: "none",
	},
	popOverContent: {
		paddingLeft: "12px",
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		paddingBottom: "12px",
	},
	cardContainer: {
		margin: "auto",
		marginBottom: "8px",
		marginTop: "8px",
		width: "90%",
	},
}));

interface CalendarContext {
	activeMonth: number;
	libraryItemsWithDate: LibraryItem[];
	handleClickInto: (item: LibraryItem) => void;
}

const ReactCalendarContext = createContext<CalendarContext>({} as CalendarContext);

const useCalendar = () => React.useContext(ReactCalendarContext);

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const shortHandDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function Transition(props: any) {
	return <Slide direction="up" {...props} />;
}

const getCalendarDatesInMonth = (monthDate: Date) => {
	const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
	const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

	let datesInCalendarMonth = DateHelper.getDatesBetween(firstDayOfMonth, lastDayOfMonth);

	let daysToAddInFront = firstDayOfMonth.getDay();
	let daysToAddBehind = 6 - lastDayOfMonth.getDay();

	if (daysToAddInFront > 0) {
		let frontDays: Date[] = [];
		let startDate = DateHelper.addDays(firstDayOfMonth, -daysToAddInFront);

		while (startDate < firstDayOfMonth) {
			frontDays.push(startDate);
			startDate = DateHelper.addDays(startDate, 1);
		}

		datesInCalendarMonth = frontDays.concat(datesInCalendarMonth);
	}

	if (daysToAddBehind > 0) {
		let endDays: Date[] = [];
		let lastDayOfTheMonth = new Date(lastDayOfMonth);
		const endDate = DateHelper.addDays(lastDayOfTheMonth, daysToAddBehind);
		lastDayOfTheMonth = DateHelper.addDays(lastDayOfMonth, 1);
		while (lastDayOfTheMonth <= endDate) {
			endDays.push(lastDayOfTheMonth);
			lastDayOfTheMonth = DateHelper.addDays(lastDayOfTheMonth, 1);
		}

		datesInCalendarMonth = datesInCalendarMonth.concat(endDays);
	}

	return datesInCalendarMonth;
};

const getMonthName = (month: number) => {
	let monthIndex = month;
	if (monthIndex < 0) {
		monthIndex = 11;
	}
	if (monthIndex > 11) {
		monthIndex = 0;
	}
	return monthNames[monthIndex];
}

export function LibraryCalendar() {
	const classes = useStyles();

	const history = useHistory();
	const isMobile = useIsMobile();
	const libraryContext = useLibrary();
	const { libraryItems } = libraryContext;

	const lastChoice = sessionStorage.getItem("calendarMonth");
	const [activeMonthDate, setActiveMonthDate] = useState<Date>(lastChoice ? new Date(lastChoice) : new Date());
	const [embeddedDialogItem, setembeddedDialogItem] = useState<LibraryItem | null>(null);
    const [downloading, setDownloading] = useState(false)

	const libraryItemsWithDate = useMemo(() => libraryItems.filter((item) => item.datePreached), [libraryItems]);
	const datesInCalendarMonth = useMemo(() => getCalendarDatesInMonth(activeMonthDate), [activeMonthDate]);
	const weeksInMonth = useMemo(
		() => [
			datesInCalendarMonth.slice(0, 7),
			datesInCalendarMonth.slice(7, 14),
			datesInCalendarMonth.slice(14, 21),
			datesInCalendarMonth.slice(21, 28),
			datesInCalendarMonth.slice(28, 35),
			datesInCalendarMonth.slice(35, 42),
		],
		[datesInCalendarMonth]
	);
	const previousMonthName = useMemo(() => getMonthName(activeMonthDate.getMonth() - 1), [activeMonthDate]);
	const nextMonthName = useMemo(() => getMonthName(activeMonthDate.getMonth() + 1), [activeMonthDate]);

	useEffect(() => {
		window.sessionStorage.setItem("calendarMonth", activeMonthDate.toString());
	}, [activeMonthDate]);

	useEffect(() => {
		const now = new Date();
		TelemetryService.updateContactField("sc_cloud_calendarinteraction", now.toString());
	}, []);

	const onPreviousMonthClick = () => {
		const currentDate = new Date(activeMonthDate);
		currentDate.setMonth(currentDate.getMonth() - 1);
		setActiveMonthDate(currentDate);


		GTag.event("Navigation", { event_category: "Library", event_label: "Calendar Change Month" });
	};

	const onNextMonthClick = () => {
		const currentDate = new Date(activeMonthDate);
		currentDate.setMonth(currentDate.getMonth() + 1);
		setActiveMonthDate(currentDate);

		GTag.event("Navigation", { event_category: "Library", event_label: "Calendar Change Month" });
	};

	const handleClickInto = async(item:LibraryItem) => {
        switch (item.type) {
			case LibraryItemType.BuilderAsset:
				return history.push(routes.app.resolve.builderPage(item.id), { prevUrl: window.location.href });

			case LibraryItemType.LinkAsset: {
				if (item.linkType! === LinkAssetType.General && item.linkUrl != null) {
					const redirectUrl = item.linkUrl.match(/^https?:\/\//i) ? item.linkUrl : "//" + item.linkUrl;
					return window.open(redirectUrl, "_blank");
				} else {
					return setembeddedDialogItem(item)
				}
			}

			case LibraryItemType.FileAsset: {
                setDownloading(true);                
                await FileAssetService.downloadFile(item.id, item.name)
                setDownloading(false);   
                return;             
            }

			default:
                return history.push(routes.app.resolve.libraryFolder(item.id))
		}
    }

	return (
		<ReactCalendarContext.Provider value={{ libraryItemsWithDate, activeMonth: activeMonthDate.getMonth(), handleClickInto }}>
			<PageTitle title="Sermon Maker - Calendar"/>
        	<div style={{height: "100%"}}>
				{!isMobile &&
					<Grid container style={{height: 45, borderBottom: "1px solid rgb(225, 225, 225)"}}>
						<Grid container item md={8} lg={9}  alignItems="center">
							<Grid item>
								<Typography style={{ color: "#5f6368", fontSize: "18px", fontWeight: 500, fontFamily: "Roboto, Helvetica"}}>
									Calendar
								</Typography>
							</Grid>
							<GridGrow />
						</Grid>
					</Grid>
				}
				<Grid container style={{height: 75}} alignItems="center" justifyContent="center">
					<Grid item>
						<Tooltip title={previousMonthName}>
							<IconButton aria-label={previousMonthName} onClick={onPreviousMonthClick}>
								<KeyboardArrowLeftIcon />
							</IconButton>
						</Tooltip>
					</Grid>
					<Grid item>
						<Typography className={classes.currentMonthText}> {getMonthName(activeMonthDate.getMonth())} {activeMonthDate.getFullYear()} </Typography>
					</Grid>
					<Grid item>
						<Tooltip title={nextMonthName}>
							<IconButton aria-label={nextMonthName} onClick={onNextMonthClick}>
								<KeyboardArrowRightIcon />
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
				<Grid container style={{height: "calc(100% - 120px)"}}>
					<table className={classes.table}>
						<thead>
							{!isMobile && (
								<tr>
									<th className={classes.tableCellWeekdays}>Sunday</th>
									<th className={classes.tableCellWeekdays}>Monday</th>
									<th className={classes.tableCellWeekdays}>Tuesday</th>
									<th className={classes.tableCellWeekdays}>Wednesday</th>
									<th className={classes.tableCellWeekdays}>Thursday</th>
									<th className={classes.tableCellWeekdays}>Friday</th>
									<th className={classes.tableCellWeekdays}>Saturday</th>
								</tr>
							)}
							{isMobile && (
								<tr>
									<th className={classes.tableCellWeekdays}>S</th>
									<th className={classes.tableCellWeekdays}>M</th>
									<th className={classes.tableCellWeekdays}>T</th>
									<th className={classes.tableCellWeekdays}>W</th>
									<th className={classes.tableCellWeekdays}>T</th>
									<th className={classes.tableCellWeekdays}>F</th>
									<th className={classes.tableCellWeekdays}>S</th>
								</tr>
							)}
						</thead>
						<tbody>
							{weeksInMonth.map((week, index) => (
								<tr className={classes.tableHeaderRow} key={index}>
									{week.map((date, index) => (
										<CalendarDate key={index} date={date} />
									))}
								</tr>
							))}
						</tbody>
					</table>
				</Grid>
			{ embeddedDialogItem &&
				<ShowEmbeddedLinkDialog 
					open={true} 
					onClose={() => setembeddedDialogItem(null)}
					linkUrl={embeddedDialogItem.linkUrl!}
					linkType={embeddedDialogItem.linkType!}
				/>
			}
			</div>
			{ downloading && <LoadingDialog text='Downloading' />}
		</ReactCalendarContext.Provider>
	);
}

interface CalendarDateProps {
	date: Date;
}

function CalendarDate(props: CalendarDateProps) {
	const isMobile = useIsMobile();
	return isMobile ? <MobileCalendarDate {...props} /> : <DesktopCalendarDate {...props} />;
}

function DesktopCalendarDate(props: CalendarDateProps) {
	const { date } = props;
	const classes = useStyles();
	const calendarContext = useCalendar();
	const { libraryItemsWithDate, activeMonth } = calendarContext;

	const itemsOnDate = useMemo(
		() => libraryItemsWithDate.filter((item) => DateHelper.sameCalendarDate(date, item.datePreached!)),
		[libraryItemsWithDate, date]
	);
	const isInActiveMonth = useMemo(() => activeMonth === date.getMonth(), [activeMonth, date]);
	const isToday = useMemo(() => DateHelper.sameCalendarDate(date, new Date()), [date]);

	const [popOverTop, setPopOverTop] = useState<number>(0);
	const [popOverLeft, setPopOverLeft] = useState<number>(0);
	const [popOverWidth, setPopOverWidth] = useState<number>(0);
	const [seeMorePopover, setSeeMorePopover] = useState<boolean>(false);
	const [seeMoreAnchorEl, setSeeMoreAnchorEl] = useState<undefined | HTMLDivElement>();
	
	const [focusedItemId, setFocusedItemId] = useState<string | undefined>();
	const [showActionPopover, setShowActionPopover] = useState(false);
    const [actionPopoverLeft, setActionPopoverLeft] = useState(0);
    const [actionPopoverTop, setActionPopoverTop] = useState(0);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const focusedItem = useMemo(() => itemsOnDate.find(i => i.id === focusedItemId), [focusedItemId]);

    const handleRightClick = (e:React.MouseEvent<HTMLElement>, item:LibraryItem) => {
        e.preventDefault();
        setFocusedItemId(item.id);
        setActionPopoverLeft(e.clientX)
        setActionPopoverTop(e.clientY)
        setShowActionPopover(true);
    }

	const onSeeMoreClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const targetDetails = e.currentTarget.getBoundingClientRect();
		// these are relative to the viewport, i.e. the window
		const top = targetDetails.top;
		const left = targetDetails.left;
		const width = targetDetails.width + 40;

		setSeeMorePopover(true);
		setSeeMoreAnchorEl(e.currentTarget);
		setPopOverLeft(left);
		setPopOverTop(top);
		setPopOverWidth(width);
	};

	return (
		<th key={date.toLocaleTimeString()} className={classNames(classes.tableCellDay, !isInActiveMonth ? classes.outOfMonthCell : "")}>
			<span className={classNames(classes.tableCellDayCount, isToday ? classes.todayCell : "")}>{date.getDate()}</span>
			<div className={classes.tableCellContent}>
				{itemsOnDate.slice(0, 3).map((innerItem: LibraryItem) => {
					return (
						<div key={innerItem.id} className={classes.tableTextRow} onClick={(e) => calendarContext.handleClickInto(innerItem)}>
							<div className={classes.tableTextBulletHolder}>
								<div className={classes.tableTextBullet} />
							</div>
							<span className={classes.tableText} onContextMenu={(e: React.MouseEvent<HTMLElement>) => handleRightClick(e, innerItem)}>
								{innerItem.name}
							</span>
						</div>
					);
				})}
				{itemsOnDate.length > 3 && (
					<div className={classes.seeMoreText} onClick={(e) => onSeeMoreClick(e)}>
						{`${itemsOnDate.length - 3} more`}
					</div>
				)}
			</div>
			<Popover
				open={seeMorePopover}
				anchorEl={seeMoreAnchorEl}
				anchorReference="anchorPosition"
				anchorPosition={{ top: popOverTop - 30, left: popOverLeft + 115 }}
				onClose={() => {
					setSeeMorePopover(false);
					setSeeMoreAnchorEl(undefined);
				}}
				anchorOrigin={{
					vertical: "center",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "center",
					horizontal: "center",
				}}
			>
				<div className={classes.popOverHeader} style={{ width: `${popOverWidth}px` }}>
					<div className={classes.popOverClose}>
						<Tooltip title="Close">
							<IconButton
								onClick={() => {
									setSeeMorePopover(false);
									setSeeMoreAnchorEl(undefined);
								}}
								aria-label="Close"
							>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</div>
					<h1 className={classes.popOverDateHolder}>
						<div className={classes.popOverDayOfWeek}>{shortHandDays[date.getDay()]}</div>
						<div className={classes.popOverDayOfMonth}>{date.getDate()}</div>
					</h1>
				</div>
				<div className={classes.popOverContent} style={{ width: `${popOverWidth - 30}px` }}>
					{itemsOnDate.map((item) => {
						return (
							<div key={item.id} className={classes.tableTextRow} onClick={(e) => calendarContext.handleClickInto(item)}>
								<div className={classes.tableTextBulletHolder}>
									<div className={classes.tableTextBullet} />
								</div>
								<span className={classes.tableText}>{item.name}</span>
							</div>
						);
					})}
				</div>
			</Popover>
			{ focusedItem &&
				<Popper
					open={showActionPopover}
					style={{position: "absolute", top: actionPopoverTop, left: actionPopoverLeft}}
				>
					<ClickAwayListener onClickAway={() => setShowActionPopover(false)}>
						<Paper elevation={2}>
							<LibraryAssetActions focusedItem={focusedItem} onClose={() => setShowActionPopover(false)} />
						</Paper>
					</ClickAwayListener>
				</Popper>
			}
		</th>
	);
}

function MobileCalendarDate(props: CalendarDateProps) {
	const { date } = props;
	const classes = useStyles();
	const calendarContext = useCalendar();
	const { libraryItemsWithDate, activeMonth } = calendarContext;

	const itemsOnDate = useMemo(
		() => libraryItemsWithDate.filter((item) => DateHelper.sameCalendarDate(date, item.datePreached!)),
		[libraryItemsWithDate, date]
	);
	const [focusedItemId, setFocusedItemId] = useState<string | undefined>();

	const focusedItem = useMemo(() => itemsOnDate.find(i => i.id === focusedItemId), [focusedItemId, itemsOnDate]);
	const isInActiveMonth = useMemo(() => activeMonth === date.getMonth(), [activeMonth, date]);
	const isToday = useMemo(() => DateHelper.sameCalendarDate(date, new Date()), [date]);
	const [mobileCalendarDayOpen, setMobileCalendarDayOpen] = useState<boolean>(false);
	
	const onClick = () => {
		if (!isInActiveMonth) {
			return;
		}
		setMobileCalendarDayOpen(true);
	};

	return (
		<>
			<th
				key={date.toLocaleTimeString()}
				className={classNames(classes.tableCellDay, !isInActiveMonth ? classes.outOfMonthCell : "")}
				onClick={onClick}
			>
				<span className={classNames(classes.tableCellDayCount, isToday ? classes.todayCell : "")}>{date.getDate()}</span>
				<div className={classes.tableCellContent}>
					{itemsOnDate.slice(0, 3).map((innerItem: LibraryItem) => {
						return (
							<div key={innerItem.id} className={classes.mobileTableTextRow}>
								<span className={classes.mobileTableText}>{innerItem.name}</span>
							</div>
						);
					})}
					{itemsOnDate.length > 3 && (
						<div className={classes.seeMoreText}>
							{`${itemsOnDate.length - 3} more`}
						</div>
					)}
				</div>
			</th>
			{mobileCalendarDayOpen && (
					<Dialog
						fullScreen
						open={mobileCalendarDayOpen}
						onClose={() => setMobileCalendarDayOpen(false)}
						TransitionComponent={Transition}
						PaperProps={{
							style: { backgroundColor: "#F3F3F3" },
						}}
					>
						<Toolbar style={{ backgroundColor: "#FFF" }}>
							<div className={classes.popOverHeader} style={{ width: "100%" }}>
								<div className={classes.popOverClose}>
									<IconButton onClick={() => setMobileCalendarDayOpen(false)} aria-label="Close">
										<CloseIcon />
									</IconButton>
								</div>
								<h1 className={classes.popOverDateHolder}>
									<div className={classes.popOverDayOfWeek}>{shortHandDays[date.getDay()]}</div>
									<div className={classes.popOverDayOfMonth}>{date.getDate()}</div>
								</h1>
							</div>
						</Toolbar>
						{itemsOnDate.length === 0 ? (
							<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
								<InsertDriveFileIcon style={{ marginTop: "50px", width: "220px", height: "220px", color: "#CCC" }} />
								<br />
								You don't have any content here.
							</Typography>
						) : <MobileLibraryAssetTable focusedItem={focusedItem} setFocusedItemId={setFocusedItemId} items={itemsOnDate} />
						}
					</Dialog>
				)}
		</>
	);
}